import { useState } from "react";
import {
  Stack,
  Box,
  Flex,
  Text,
  Button,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Input,
  InputGroup,
  InputLeftElement,
  Badge,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  Select,
} from "@chakra-ui/react";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import { FiSearch, FiPlus } from "react-icons/fi";

const FAQs = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchInput, setSearchInput] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  // Mock data
  const faqs = [
    {
      id: "1",
      question: "How do I book a ride?",
      answer: "You can book a ride through our app by entering your pickup and drop-off locations, selecting a vehicle type, and confirming the booking.",
      category: "Booking",
      isPublished: true,
    },
    {
      id: "2",
      question: "What payment methods are accepted?",
      answer: "We accept cash, credit/debit cards, UPI, and wallet payments. You can manage your payment methods in the app settings.",
      category: "Payments",
      isPublished: true,
    },
    {
      id: "3",
      question: "How can I cancel my ride?",
      answer: "You can cancel your ride from the app by going to your active booking and clicking the cancel button. Please note that cancellation charges may apply.",
      category: "Booking",
      isPublished: true,
    },
    {
      id: "4",
      question: "How do I become a driver partner?",
      answer: "You can register as a driver partner through our driver app or website. You'll need to submit your documents for verification.",
      category: "Driver",
      isPublished: false,
    },
  ];

  const categories = ["all", "Booking", "Payments", "Driver", "General"];

  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = categoryFilter === "all" || faq.category === categoryFilter;
    const matchesSearch = !searchInput || 
      faq.question.toLowerCase().includes(searchInput.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchInput.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <Stack p={6} gap={4}>
      <BreadCrumbMaker
        items={[
          { link: "/support", title: "Support Center" },
          { link: "#", title: "FAQs", on: true },
        ]}
      />

      <Box bg="white" borderRadius="xl" p={4} shadow="sm">
        <Flex justify="space-between" align="center" mb={4}>
          <Text fontSize="xl" fontWeight="bold" color="gray.700">
            Frequently Asked Questions
          </Text>
          <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={onOpen}>
            Add FAQ
          </Button>
        </Flex>

        <Flex gap={4} mb={4}>
          <InputGroup maxW="400px">
            <InputLeftElement>
              <FiSearch color="gray" />
            </InputLeftElement>
            <Input
              placeholder="Search FAQs..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              borderRadius="lg"
            />
          </InputGroup>
          <Select
            maxW="200px"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>
                {cat === "all" ? "All Categories" : cat}
              </option>
            ))}
          </Select>
        </Flex>

        <Accordion allowMultiple>
          {filteredFaqs.length === 0 ? (
            <Box textAlign="center" py={8}>
              <Text color="gray.500">No FAQs found</Text>
            </Box>
          ) : (
            filteredFaqs.map((faq) => (
              <AccordionItem key={faq.id} border="1px solid" borderColor="gray.200" borderRadius="lg" mb={2}>
                <h2>
                  <AccordionButton _expanded={{ bg: "blue.50" }} borderRadius="lg">
                    <Box flex="1" textAlign="left" fontWeight="medium">
                      {faq.question}
                    </Box>
                    <Flex gap={2} mr={2}>
                      <Badge colorScheme="blue">{faq.category}</Badge>
                      <Badge colorScheme={faq.isPublished ? "green" : "gray"}>
                        {faq.isPublished ? "Published" : "Draft"}
                      </Badge>
                    </Flex>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4} bg="gray.50">
                  <Text color="gray.700">{faq.answer}</Text>
                  <Flex gap={2} mt={4}>
                    <Button size="sm" variant="outline">Edit</Button>
                    <Button size="sm" variant="outline" colorScheme="red">Delete</Button>
                  </Flex>
                </AccordionPanel>
              </AccordionItem>
            ))
          )}
        </Accordion>
      </Box>

      {/* Add FAQ Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New FAQ</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Question</FormLabel>
                <Input placeholder="Enter the question" />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Answer</FormLabel>
                <Textarea placeholder="Enter the answer" rows={4} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Category</FormLabel>
                <Select placeholder="Select category">
                  <option value="Booking">Booking</option>
                  <option value="Payments">Payments</option>
                  <option value="Driver">Driver</option>
                  <option value="General">General</option>
                </Select>
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue">Save as Draft</Button>
            <Button colorScheme="green" ml={2}>Publish</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
};

export default FAQs;
